import type { CuttingStrategy } from './CuttingStrategy';
import type { IMaterial, DebugStep } from '~/types/Material';
import type { ICutResult } from '~/types/CutResult';

interface OrientationCombo {
    name: string;
    part1Landscape: boolean;
    part2Landscape: boolean;
    wastePercentage: number;
    utilizationPercentage: number;
}

interface Rectangle {
    x: number;
    y: number;
    width: number;
    height: number;
}

export class RowBasedOrientationStrategy implements CuttingStrategy {
    name = 'Row-Based Orientation';
    visualizationCounter = 0;
    debugSteps: DebugStep[] = [];
    placedParts: IMaterial[] = [];

    optimize(material: IMaterial, parts: IMaterial[]): ICutResult {
        // Reset tracking variables
        this.debugSteps = [];
        this.visualizationCounter = 0;
        this.placedParts = [];

        const usedMaterials: IMaterial[] = [];
        const debugSteps: DebugStep[] = [];

        // If we have less than two parts, use simple placement
        if (parts.length < 2) {
            return this.simplePlacement(material, [...parts]);
        }

        // Get unique part types (ignoring duplicates)
        const uniquePartTypes = this.getUniquePartTypes(parts);

        // If we have only one part type, use simple placement
        if (uniquePartTypes.length < 2) {
            return this.simplePlacement(material, [...parts]);
        }

        // Select first two part types for orientation analysis
        const part1 = uniquePartTypes[0];
        const part2 = uniquePartTypes[1];

        debugSteps.push({
            type: 'check',
            reason: `Selected parts for orientation analysis:
            - Part 1 (${part1.width}x${part1.height}): ${part1.width}x${part1.height}
            - Part 2 (${part2.width}x${part2.height}): ${part2.width}x${part2.height}`
        });

        // Create a copy of parts for placement
        const remainingParts = [...parts];

        // Analyze all four possible orientation combinations
        const combos: OrientationCombo[] = [
            {
                name: 'Landscape & Landscape',
                part1Landscape: true,
                part2Landscape: true,
                wastePercentage: 0,
                utilizationPercentage: 0
            },
            {
                name: 'Landscape & Portrait',
                part1Landscape: true,
                part2Landscape: false,
                wastePercentage: 0,
                utilizationPercentage: 0
            },
            {
                name: 'Portrait & Landscape',
                part1Landscape: false,
                part2Landscape: true,
                wastePercentage: 0,
                utilizationPercentage: 0
            },
            {
                name: 'Portrait & Portrait',
                part1Landscape: false,
                part2Landscape: false,
                wastePercentage: 0,
                utilizationPercentage: 0
            }
        ];

        // Test each orientation combination
        for (const combo of combos) {
            const result = this.analyzeOrientationCombo(
                material.width,
                material.height,
                part1,
                part2,
                combo.part1Landscape,
                combo.part2Landscape
            );

            combo.wastePercentage = result.wastePercentage;
            combo.utilizationPercentage = result.utilizationPercentage;

            // Add debug step
            debugSteps.push({
                type: 'check',
                reason: `Testing ${combo.name}: waste ${combo.wastePercentage.toFixed(2)}%, utilization ${combo.utilizationPercentage.toFixed(2)}%`
            });
        }

        // Sort combos by waste percentage (lowest first)
        combos.sort((a, b) => a.wastePercentage - b.wastePercentage);

        // Choose the best combo (lowest waste)
        const bestCombo = combos[0];
        const isBestComboPortraitLandscape = bestCombo.name === 'Portrait & Landscape';

        debugSteps.push({
            type: 'check',
            reason: `Selected orientation: ${bestCombo.name} with waste ${bestCombo.wastePercentage.toFixed(2)}%, utilization ${bestCombo.utilizationPercentage.toFixed(2)}%`
        });

        // Add visualization step
        const visualId = `orientation-comparison-${this.visualizationCounter++}`;
        debugSteps.push({
            type: 'freeRectangles',
            reason: `Orientation Comparison: ${bestCombo.name} selected with ${bestCombo.wastePercentage.toFixed(2)}% waste`,
            visualizationId: visualId
        });

        // Use row-based placement with the optimal orientations
        while (remainingParts.length > 0) {
            const currentSheet: IMaterial = {
                ...material,
                id: `material-${usedMaterials.length + 1}`,
                children: [],
            };

            // Add debug step for new sheet
            debugSteps.push({
                type: 'check',
                reason: `Creating new material sheet #${usedMaterials.length + 1} (${material.width}x${material.height})`
            });

            if (isBestComboPortraitLandscape) {
                // Special handling for Portrait & Landscape combination
                // Here we'll explicitly place first row with part1 in portrait orientation
                // and second row with part2 in landscape orientation

                let currentY = 0;

                // First row - Part1 in Portrait orientation
                const part1BaseId = this.getBaseId(part1.id);
                const part1Group = this.getPartsByBaseId(remainingParts, part1BaseId);

                if (part1Group.length > 0) {
                    const partWidth = part1.height; // Portrait means width and height are swapped
                    const partHeight = part1.width;

                    const count = Math.min(
                        Math.floor(material.width / partWidth),
                        part1Group.length
                    );

                    if (count > 0) {
                        this.placePartsInRow(
                            currentSheet,
                            part1Group,
                            false, // Portrait
                            count,
                            currentY,
                            remainingParts,
                            debugSteps
                        );

                        currentY += partHeight;

                        debugSteps.push({
                            type: 'check',
                            reason: `Placed first row with ${count} Part1 items in Portrait orientation at Y=${0}`
                        });
                    }
                }

                // Second row - Part2 in Landscape orientation
                const part2BaseId = this.getBaseId(part2.id);
                const part2Group = this.getPartsByBaseId(remainingParts, part2BaseId);

                if (part2Group.length > 0) {
                    const partWidth = part2.width; // Landscape orientation
                    const partHeight = part2.height;

                    // Check if there's enough height left
                    if (currentY + partHeight <= material.height) {
                        const count = Math.min(
                            Math.floor(material.width / partWidth),
                            part2Group.length
                        );

                        if (count > 0) {
                            this.placePartsInRow(
                                currentSheet,
                                part2Group,
                                true, // Landscape
                                count,
                                currentY,
                                remainingParts,
                                debugSteps
                            );

                            currentY += partHeight;

                            debugSteps.push({
                                type: 'check',
                                reason: `Placed second row with ${count} Part2 items in Landscape orientation at Y=${currentY - partHeight}`
                            });
                        }
                    }
                }

                // Place any remaining parts using standard algorithm
                if (remainingParts.length > 0 && currentY < material.height) {
                    // Continue with remaining height using standard row-based placement
                    this.placeRemainingParts(
                        currentSheet,
                        remainingParts,
                        material,
                        currentY,
                        bestCombo,
                        uniquePartTypes,
                        debugSteps
                    );
                }
            } else {
                // Set the orientation preferences based on our analysis
                const orientationPrefs = new Map<string, boolean>();

                // Set orientation preferences for all part types based on their IDs
                for (const part of uniquePartTypes) {
                    if (this.isSameDimensions(part, part1)) {
                        orientationPrefs.set(this.getBaseId(part.id), bestCombo.part1Landscape);
                    } else if (this.isSameDimensions(part, part2)) {
                        orientationPrefs.set(this.getBaseId(part.id), bestCombo.part2Landscape);
                    }
                }

                // Track our position on the sheet
                let currentY = 0;
                let rowHeight = 0;
                let placedAny = false;

                // Place parts row by row
                while (currentY < material.height && remainingParts.length > 0) {
                    const remainingHeight = material.height - currentY;

                    // Find the best part and orientation for the current row
                    const result = this.findBestPartForRow(
                        remainingParts,
                        material.width,
                        remainingHeight,
                        orientationPrefs
                    );

                    if (!result || result.count === 0) {
                        break; // No more parts fit
                    }

                    placedAny = true;
                    const { part, isLandscape, count } = result;

                    // Height for this row
                    rowHeight = isLandscape ? part.height : part.width;

                    // Place the parts in this row
                    this.placePartsInRow(currentSheet, [part], isLandscape, count, currentY, remainingParts, debugSteps);

                    // Move to next row
                    currentY += rowHeight;
                }

                if (!placedAny) {
                    break; // Couldn't place any parts on a new sheet
                }
            }

            // Add the current sheet to used materials
            usedMaterials.push(currentSheet);
        }

        // Calculate waste
        const totalMaterialArea = usedMaterials.length * material.width * material.height;
        const totalPartsArea = parts.reduce((sum, part) => sum + part.width * part.height, 0);
        const wastePercentage = ((totalMaterialArea - totalPartsArea) / totalMaterialArea) * 100;

        return {
            usedMaterials,
            wastePercentage,
            strategyName: this.name,
            debugSteps
        };
    }

    /**
     * Analyze a specific orientation combination
     */
    private analyzeOrientationCombo(
        materialWidth: number,
        materialHeight: number,
        part1: IMaterial,
        part2: IMaterial,
        part1Landscape: boolean,
        part2Landscape: boolean
    ): { wastePercentage: number; utilizationPercentage: number } {
        // First test - part1 orientation
        const part1Width = part1Landscape ? part1.width : part1.height;
        const part1Height = part1Landscape ? part1.height : part1.width;

        const part1FitCount = Math.floor(materialWidth / part1Width);
        const part1RowWidth = part1FitCount * part1Width;
        const part1WasteWidth = materialWidth - part1RowWidth;
        const part1WastePercentage = part1WasteWidth / materialWidth * 100;
        const part1UtilizationPercentage = 100 - part1WastePercentage;

        // Second test - part2 orientation
        const part2Width = part2Landscape ? part2.width : part2.height;
        const part2Height = part2Landscape ? part2.height : part2.width;

        const part2FitCount = Math.floor(materialWidth / part2Width);
        const part2RowWidth = part2FitCount * part2Width;
        const part2WasteWidth = materialWidth - part2RowWidth;
        const part2WastePercentage = part2WasteWidth / materialWidth * 100;
        const part2UtilizationPercentage = 100 - part2WastePercentage;

        // Calculate average waste and utilization
        const wastePercentage = (part1WastePercentage + part2WastePercentage) / 2;
        const utilizationPercentage = (part1UtilizationPercentage + part2UtilizationPercentage) / 2;

        return { wastePercentage, utilizationPercentage };
    }

    /**
     * Find the best part to place on the current row
     */
    private findBestPartForRow(
        parts: IMaterial[],
        rowWidth: number,
        rowHeight: number,
        orientationPrefs: Map<string, boolean>
    ) {
        let bestPart: IMaterial | null = null;
        let bestCount = 0;
        let bestWaste = Infinity;
        let bestIsLandscape = false;

        // Group parts by their base ID
        const partGroups = this.groupPartsByBaseId(parts);

        for (const [baseId, partsGroup] of partGroups) {
            const part = partsGroup[0]; // Take first part as reference
            const preferredLandscape = orientationPrefs.get(baseId);

            // If we have a preference, only try that orientation
            const orientationsToTry = preferredLandscape !== undefined
                ? [preferredLandscape]
                : [true, false]; // Try both if no preference

            for (const isLandscape of orientationsToTry) {
                const partWidth = isLandscape ? part.width : part.height;
                const partHeight = isLandscape ? part.height : part.width;

                if (partHeight > rowHeight) continue; // Part doesn't fit in row height

                const count = Math.min(
                    Math.floor(rowWidth / partWidth), // How many fit in the row
                    partsGroup.length // How many we have available
                );

                if (count === 0) continue; // None fit

                const usedWidth = count * partWidth;
                const waste = (rowWidth - usedWidth) / rowWidth;

                // Choose the option with least waste, or in case of tie, most parts
                if (count > 0 && (waste < bestWaste || (waste === bestWaste && count > bestCount))) {
                    bestPart = part;
                    bestCount = count;
                    bestWaste = waste;
                    bestIsLandscape = isLandscape;
                }
            }
        }

        return bestPart ? { part: bestPart, isLandscape: bestIsLandscape, count: bestCount } : null;
    }

    /**
     * Group parts by their base ID (removing instance numbers)
     */
    private groupPartsByBaseId(parts: IMaterial[]): Map<string, IMaterial[]> {
        const groups = new Map<string, IMaterial[]>();

        for (const part of parts) {
            const baseId = this.getBaseId(part.id);
            if (!groups.has(baseId)) {
                groups.set(baseId, []);
            }
            groups.get(baseId)!.push(part);
        }

        return groups;
    }

    /**
     * Get the base ID without instance number (e.g., "part-1-2" → "part-1")
     */
    private getBaseId(id: string): string {
        // Extract the base part ID by removing the last number segment
        // This handles formats like "part-1-2" where "part-1" is the base ID
        const match = id.match(/^(.*?)(?:-\d+)?$/);
        return match ? match[1] : id;
    }

    /**
     * Check if two parts have the same dimensions
     */
    private isSameDimensions(part1: IMaterial, part2: IMaterial): boolean {
        return (
            (part1.width === part2.width && part1.height === part2.height) ||
            (part1.width === part2.height && part1.height === part2.width)
        );
    }

    /**
     * Get unique part types based on dimensions
     */
    private getUniquePartTypes(parts: IMaterial[]): IMaterial[] {
        const uniqueParts: IMaterial[] = [];
        const dimensionSets = new Set<string>();

        for (const part of parts) {
            // Create dimension string in canonical form (width ≤ height)
            const minDim = Math.min(part.width, part.height);
            const maxDim = Math.max(part.width, part.height);
            const dimensionKey = `${minDim}x${maxDim}`;

            if (!dimensionSets.has(dimensionKey)) {
                dimensionSets.add(dimensionKey);
                uniqueParts.push(part);
            }
        }

        return uniqueParts;
    }

    /**
     * Simple placement algorithm for fallback
     */
    private simplePlacement(material: IMaterial, parts: IMaterial[]): ICutResult {
        // Basic implementation for single part type
        const usedMaterials: IMaterial[] = [];
        let currentSheet: IMaterial | null = null;
        let currentX = 0;
        let currentY = 0;
        let rowHeight = 0;

        for (const part of parts) {
            // Try both orientations
            const landscapeWidth = part.width;
            const landscapeHeight = part.height;
            const portraitWidth = part.height;
            const portraitHeight = part.width;

            // Determine which orientation fits better
            const landscapeFits = landscapeWidth <= material.width && landscapeHeight <= material.height;
            const portraitFits = portraitWidth <= material.width && portraitHeight <= material.height;

            if (!landscapeFits && !portraitFits) {
                // Part doesn't fit in any orientation
                continue;
            }

            const isLandscape = landscapeFits &&
                (!portraitFits || landscapeWidth <= portraitWidth);

            const width = isLandscape ? landscapeWidth : portraitWidth;
            const height = isLandscape ? landscapeHeight : portraitHeight;

            // Check if we need a new sheet or a new row
            if (!currentSheet || currentX + width > material.width) {
                // Try to start a new row
                if (currentSheet && currentY + rowHeight + height <= material.height) {
                    currentX = 0;
                    currentY += rowHeight;
                    rowHeight = height;
                } else {
                    // Start a new sheet
                    currentSheet = {
                        ...material,
                        id: `material-${usedMaterials.length + 1}`,
                        children: [],
                    };
                    usedMaterials.push(currentSheet);
                    currentX = 0;
                    currentY = 0;
                    rowHeight = height;
                }
            }

            // Update row height if needed
            rowHeight = Math.max(rowHeight, height);

            // Place the part
            currentSheet.children!.push({
                ...part,
                x: currentX,
                y: currentY,
                rotated: isLandscape !== (part.width > part.height),
                width,
                height
            });

            // Move to next position
            currentX += width;
        }

        // Calculate waste
        const totalMaterialArea = usedMaterials.length * material.width * material.height;
        const totalPartsArea = parts.reduce((sum, part) => sum + part.width * part.height, 0);
        const wastePercentage = ((totalMaterialArea - totalPartsArea) / totalMaterialArea) * 100;

        return {
            usedMaterials,
            wastePercentage,
            strategyName: this.name,
            debugSteps: []
        };
    }

    /**
     * Get parts by their base ID from the remaining parts list
     */
    private getPartsByBaseId(parts: IMaterial[], baseId: string): IMaterial[] {
        return parts.filter(part => this.getBaseId(part.id) === baseId);
    }

    /**
     * Place parts in a row with specified orientation
     */
    private placePartsInRow(
        currentSheet: IMaterial,
        parts: IMaterial[],
        isLandscape: boolean,
        count: number,
        currentY: number,
        remainingParts: IMaterial[],
        debugSteps: DebugStep[]
    ): void {
        // Width and height of the part in the chosen orientation
        const part = parts[0];
        const partWidth = isLandscape ? part.width : part.height;
        const partHeight = isLandscape ? part.height : part.width;

        // Place the parts in this row
        for (let i = 0; i < count; i++) {
            // Find the corresponding part instance in remainingParts
            const partIndex = remainingParts.findIndex(p =>
                this.getBaseId(p.id) === this.getBaseId(part.id));

            if (partIndex === -1) break;

            const placedPart = remainingParts[partIndex];
            remainingParts.splice(partIndex, 1);

            const x = i * partWidth;
            const placedPartObj: IMaterial = {
                ...placedPart,
                x: x,
                y: currentY,
                rotated: isLandscape !== (placedPart.width > placedPart.height),
                width: partWidth,
                height: partHeight
            };

            currentSheet.children!.push(placedPartObj);
            this.placedParts.push(placedPartObj);

            debugSteps.push({
                type: 'place',
                part: placedPartObj,
                position: { x, y: currentY },
                reason: `Placed part ${placedPart.id} at (${x}, ${currentY}) with orientation ${isLandscape ? 'landscape' : 'portrait'}`
            });
        }
    }

    /**
     * Place remaining parts using standard row-based algorithm
     */
    private placeRemainingParts(
        currentSheet: IMaterial,
        remainingParts: IMaterial[],
        material: IMaterial,
        startY: number,
        bestCombo: OrientationCombo,
        uniquePartTypes: IMaterial[],
        debugSteps: DebugStep[]
    ): void {
        // Set the orientation preferences based on our analysis
        const orientationPrefs = new Map<string, boolean>();
        const part1 = uniquePartTypes[0];
        const part2 = uniquePartTypes[1];

        // Set orientation preferences for all part types based on their IDs
        for (const part of uniquePartTypes) {
            if (this.isSameDimensions(part, part1)) {
                orientationPrefs.set(this.getBaseId(part.id), bestCombo.part1Landscape);
            } else if (this.isSameDimensions(part, part2)) {
                orientationPrefs.set(this.getBaseId(part.id), bestCombo.part2Landscape);
            }
        }

        let currentY = startY;
        let rowHeight = 0;

        // Place parts row by row
        while (currentY < material.height && remainingParts.length > 0) {
            const remainingHeight = material.height - currentY;

            // Find the best part and orientation for the current row
            const result = this.findBestPartForRow(
                remainingParts,
                material.width,
                remainingHeight,
                orientationPrefs
            );

            if (!result || result.count === 0) {
                break; // No more parts fit
            }

            const { part, isLandscape, count } = result;

            // Height for this row
            rowHeight = isLandscape ? part.height : part.width;

            // Place the parts in this row
            this.placePartsInRow(currentSheet, [part], isLandscape, count, currentY, remainingParts, debugSteps);

            // Move to next row
            currentY += rowHeight;
        }
    }
}
